import React from "react";
import {DataGrid, GridColDef, GridActionsCellItem, GridToolbarContainer, GridRenderCellParams} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import {Button, Chip, Tooltip, Typography} from "@mui/material";
import {Box} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import IServiceOrder from "../../../types/service-order.type";
import AddIcon from "@mui/icons-material/Add";
import IBudget from "../../../types/budget.type";
import BudgetModal from "./BudgetModal";
import IUser from "../../../types/user.type";
import ConfirmationModal from "../../utils/ConfirmationModal";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {v4 as uuidv4} from "uuid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import IProvider from "../../../types/provider.type";
import {CashBoxStatusColors} from "../../../types/enumeration/CashBoxStatusColors";
import {CashBoxStatus} from "../../../types/enumeration/CashBoxStatus";
import {CashBoxStatusNames} from "../../../types/enumeration/CashBoxStatusNames";
import {BudgetConceptNames} from "../../../types/enumeration/BudgetConceptNames";
import {BudgetConcept} from "../../../types/enumeration/BudgetConcept";

interface Props {
  serviceOrder: IServiceOrder;
  setServiceOrder: (updatedServiceOrder: IServiceOrder | ((prev: IServiceOrder) => IServiceOrder)) => void;
  users: IUser[],
  providers: IProvider[],
  readOnly?: boolean;
}

const BudgetsList: React.FC<Props> = ({serviceOrder, setServiceOrder, users, providers, readOnly = false}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [selectedBudget, setSelectedBudget] = React.useState<IBudget>();

  const handleConfirmationModalClose = () => {
    setSelectedBudget(undefined);
    setConfirmationModalOpen(false);
  }

  const handleDeleteClick = (budget: IBudget) => () => {
    setSelectedBudget(budget);
    setConfirmationModalOpen(true);
  };

  const onConfirm = () => {
    setServiceOrder(prev => ({
      ...prev,
      budgets: prev.budgets.filter(ws => ws.id !== selectedBudget?.id),
    }));

    setSelectedBudget(undefined);
    setConfirmationModalOpen(false);
  };

  const handleModalClose = () => {
    setSelectedBudget(undefined);
    setModalOpen(false);
  }

  const handleAddClick = () => {
    setSelectedBudget(undefined);
    setModalOpen(true);
  };

  const handleEditClick = (budget: IBudget) => () => {
    setSelectedBudget(budget);
    setModalOpen(true);
  };

  const onSubmit = (budget: IBudget) => {
    const id =
      budget.id
      ?? globalThis.crypto?.randomUUID?.()
      ?? uuidv4();

    const newContact = {
      ...budget,
      id,
    };

    setServiceOrder(prev => ({
      ...prev,
      budgets: prev.budgets.some(contact => contact.id === newContact.id)
        ? prev.budgets.map(contact => contact.id === newContact.id ? newContact : contact)
        : [...prev.budgets, newContact],
    }));

    setSelectedBudget(undefined);
    setModalOpen(false);
  };

  const columns: GridColDef<IBudget>[] = [
    {
      field: 'concept',
      headerName: 'Concepto',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span>{BudgetConceptNames[BudgetConcept[params.row.concept as keyof typeof BudgetConcept]]}</span>;
      }
    },
    {
      field: 'amount',
      headerName: 'Monto',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'resources',
      headerName: 'Recursos',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.concept === BudgetConcept.SUBCONTRACT) {
          return (
            <Tooltip
              title={
                <Typography>
                  {params.row.providers.map((provider: { name: string }, index: number) => (
                    <div key={index}>- {provider.name}</div>
                  ))}
                </Typography>
              }
              arrow
              placement='right'
            >
              <Chip label={`${params.row.providers?.length || 0}`} size="small" sx={{ marginTop: -0.2 }}/>
            </Tooltip>
          )
        } else {
          return (
            <Tooltip
              title={
                <Typography>
                  {params.row.resources.map((user: { lastName: string; name: string }, index: number) => (
                    <div key={index}>- {user.lastName}, {user.name}</div>
                  ))}
                </Typography>
              }
              arrow
              placement='right'
            >
              <Chip label={`${params.row.resources?.length || 0}`} size="small" sx={{ marginTop: -0.2 }}/>
            </Tooltip>
          )
        }
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: (params) => {
        if (readOnly){
          return [
            <GridActionsCellItem
              icon={<VisibilityIcon/>}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(params.row)}
              color="inherit"
            />
          ];
        } else {
          return [
            <GridActionsCellItem
              icon={<EditIcon/>}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(params.row)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<DeleteForeverIcon/>}
              label="Delete"
              className="textPrimary"
              onClick={handleDeleteClick(params.row)}
              color="inherit"
            />
          ];
        }
      },
    },
  ];

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          endIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={readOnly}
        >
          Agregar Presupuesto
        </Button>
      </GridToolbarContainer>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 384, typography: 'body1' }} mt={1}>

      <ConfirmationModal
        open={confirmationModalOpen}
        onConfirm={onConfirm}
        onCancel={handleConfirmationModalClose} />

      <BudgetModal
        isOpen={modalOpen}
        onSubmit={onSubmit}
        onClose={handleModalClose}
        budget={selectedBudget}
        users={users}
        providers={providers}
        readOnly={readOnly}
      />

      <CssVarsProvider>
        <DataGrid
          slots={{toolbar: EditToolbar}}
          rows={serviceOrder.budgets}
          columns={columns}
          rowHeight={25}
          columnHeaderHeight={30}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10]}
          localeText={{ noRowsLabel: "No Hay Presupuestos" }}
          disableRowSelectionOnClick
        />

      </CssVarsProvider>
    </Box>
  );
};

export default BudgetsList;
